// react/utils
import {
  differenceInCalendarDays,
  differenceInMinutes,
  startOfDay
} from 'date-fns';
import { usePeriodInputState } from './usePeriodInputState';
import { usePeriodInputSyncReporting } from './usePeriodInputSyncReporting';

// types
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import type { Period, PeriodChangeReason } from '../PeriodInput.types';

type UsePeriodInputControllerArgs = {
  period: Period;
  sameDayOnly?: boolean;
  onChange: (period: Period, reason: PeriodChangeReason) => void;
};

/**
 * The component's only entry point: composes the state and the reporting choreography, and
 * wraps them in the handlers the fields need. Every handler is the same two steps -- mark the
 * change as the user's, then write it -- which is what keeps a typed value distinguishable
 * from a period this component derived on its own.
 */
export const usePeriodInputController = ({
  period,
  sameDayOnly,
  onChange
}: UsePeriodInputControllerArgs) => {
  'use memo';

  const {
    startDate,
    startTime,
    durationHours,
    durationMinutes,
    setStartDate,
    setStartTime,
    setDurationHours,
    setDurationMinutes,
    syncFromProps,
    fullStart,
    fullEnd,
    minEndTime,
    maxEndTime,
    propsStartMs,
    propsEndMs,
    fullStartMs,
    fullEndMs,
    fullEndMinuteMs
  } = usePeriodInputState({ period, sameDayOnly });

  const { markUserEdited } = usePeriodInputSyncReporting({
    startDate,
    startTime,
    durationHours,
    durationMinutes,
    propsStartMs,
    propsEndMs,
    fullStartMs,
    fullEndMs,
    fullEndMinuteMs,
    syncFromProps,
    onChange
  });

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;

    markUserEdited();
    setStartDate(startOfDay(newDate));
  };

  const handleStartTimeChange = (newTime: Date | null) => {
    if (!newTime) return;

    markUserEdited();
    setStartTime(newTime);
  };

  const handleEndTimeChange = (newTime: Date | null) => {
    if (!newTime || !fullStart) return;

    const diffMinutes = differenceInMinutes(newTime, fullStart);
    const clamped = Math.max(diffMinutes, 0);

    markUserEdited();
    setDurationHours(String(Math.floor(clamped / 60)));
    setDurationMinutes(String(clamped % 60));
  };

  const updateTimeValue = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    limit: number,
    setter: Dispatch<SetStateAction<string>>
  ) => {
    const value = ev.currentTarget.value;
    if (value.length > 2) return;

    let parsed = parseInt(value);
    if (isNaN(parsed)) parsed = 0;
    parsed = Math.max(0, Math.min(parsed, limit - 1));

    markUserEdited();
    setter(String(parsed));
  };

  const handleHoursChange = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => updateTimeValue(ev, 24, setDurationHours);

  const handleMinutesChange = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => updateTimeValue(ev, 60, setDurationMinutes);

  // How many days past the start date the end lands on, for the `+n` badge on the end field.
  const overflowDays =
    !fullEnd || !startTime
      ? 0
      : differenceInCalendarDays(startOfDay(fullEnd), startOfDay(startTime));

  return {
    startDate,
    startTime,
    durationHours,
    durationMinutes,
    fullEnd,
    minEndTime,
    maxEndTime,
    overflowDays,
    handleDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleHoursChange,
    handleMinutesChange
  };
};
